import React, { useEffect, useMemo, useState } from "react";
import { Cloud, Timer, Zap, PackageX, MapPin, Plus, X, Sparkles } from "lucide-react";

/**
 * <RuleBuilder />
 *
 * Reusable programmable-logic composer used by:
 *   • /programmable (demo, standalone)
 *   • Ads Dashboard (attached to a real campaign)
 *
 * Rule shape: { id, input, value, action, creative }
 *   input   ∈ weather | time | event | inventory | venue
 *   action  ∈ show | pause | boost
 *
 * Emits `onChange(rules)` on every mutation.
 * Optional `simulator=true` renders the live environment simulator + firing preview.
 */

export const RULE_INPUTS = {
  weather: { label: "Weather", icon: Cloud, options: ["rain", "sunny", "snow", "cloudy"], color: "#00E5FF" },
  time: { label: "Time", icon: Timer, options: ["morning", "lunch", "evening", "late-night"], color: "#E8D6A3" },
  event: { label: "Local event", icon: Zap, options: ["football", "concert", "festival", "none"], color: "#CCFF00" },
  inventory: { label: "Inventory", icon: PackageX, options: ["available", "low", "out_of_stock"], color: "#ff6b6b" },
  venue: { label: "Venue type", icon: MapPin, options: ["café", "airport", "mall", "gym"], color: "#a78bfa" },
};

export const RULE_ACTIONS = {
  show: { label: "SHOW", color: "text-[#E8D6A3]" },
  pause: { label: "PAUSE", color: "text-red-400" },
  boost: { label: "BOOST spend", color: "text-[#00E5FF]" },
};

const ENVIRONMENTS = [
  { weather: "rain", time: "morning", event: "none", inventory: "available", venue: "café" },
  { weather: "sunny", time: "lunch", event: "concert", inventory: "available", venue: "mall" },
  { weather: "cloudy", time: "evening", event: "football", inventory: "low", venue: "gym" },
  { weather: "sunny", time: "late-night", event: "none", inventory: "out_of_stock", venue: "airport" },
  { weather: "snow", time: "morning", event: "festival", inventory: "available", venue: "café" },
];

function newRule(input = "weather") {
  return {
    id: `r_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
    input,
    value: RULE_INPUTS[input].options[0],
    action: "show",
    creative: "",
  };
}

export default function RuleBuilder({
  rules,
  onChange,
  simulator = false,
  emptyHint = "No rules yet. Add one — advertising becomes executable logic.",
  testidPrefix = "rule",
}) {
  const [envIdx, setEnvIdx] = useState(0);
  const [running, setRunning] = useState(simulator);

  useEffect(() => {
    if (!simulator || !running) return;
    const t = setInterval(
      () => setEnvIdx((i) => (i + 1) % ENVIRONMENTS.length),
      2400
    );
    return () => clearInterval(t);
  }, [simulator, running]);

  const env = ENVIRONMENTS[envIdx];
  const activeRule = useMemo(
    () => rules.find((r) => env[r.input] === r.value) || null,
    [rules, env]
  );

  const patchRule = (id, patch) =>
    onChange(rules.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const addRule = () => onChange([...rules, newRule()]);
  const importPending = () => {
    try {
      const raw = localStorage.getItem("mirrorloops_pending_rule_import");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.input || !RULE_INPUTS[parsed.input]) return null;
      const imported = {
        id: `r_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
        input: parsed.input,
        value: parsed.value || RULE_INPUTS[parsed.input].options[0],
        action: parsed.action || "show",
        creative: parsed.creative || "",
      };
      onChange([...rules, imported]);
      localStorage.removeItem("mirrorloops_pending_rule_import");
      return imported;
    } catch {
      return null;
    }
  };
  const hasPending = (() => {
    try {
      return !!localStorage.getItem("mirrorloops_pending_rule_import");
    } catch {
      return false;
    }
  })();
  const removeRule = (id) => onChange(rules.filter((r) => r.id !== id));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 flex items-center gap-2">
          <Sparkles size={12} className="text-[#E8D6A3]" /> Programmable rules
          <span className="text-white/30">·</span>
          <span className="font-mono text-[#E8D6A3]">{rules.length}</span>
        </div>
        <button
          type="button"
          onClick={addRule}
          data-testid={`${testidPrefix}-add`}
          className="text-xs px-3 py-1.5 border border-white/15 hover:border-white transition inline-flex items-center gap-1"
        >
          <Plus size={12} /> Add rule
        </button>
      </div>

      {hasPending && (
        <button
          type="button"
          onClick={importPending}
          data-testid={`${testidPrefix}-import-pending`}
          className="w-full text-xs px-3 py-2 border border-[#D4B878]/40 bg-[#D4B878]/10 text-[#E8D6A3] hover:bg-[#D4B878]/20 transition inline-flex items-center justify-center gap-2"
        >
          <Sparkles size={12} /> A rule template is staged from the marketplace
          — tap to insert
        </button>
      )}

      {rules.length === 0 && (
        <div
          className="p-6 border border-dashed border-white/10 text-center text-white/40 text-sm"
          data-testid={`${testidPrefix}-empty`}
        >
          {emptyHint}
        </div>
      )}

      <div className="space-y-2" data-testid={`${testidPrefix}-list`}>
        {rules.map((r) => {
          const cat = RULE_INPUTS[r.input] || RULE_INPUTS.weather;
          const Icon = cat.icon;
          const active = simulator && activeRule?.id === r.id;
          return (
            <div
              key={r.id}
              data-testid={`${testidPrefix}-row-${r.id}`}
              className={`p-3 border transition font-mono text-sm ${
                active
                  ? "border-[#E8D6A3] bg-[#D4B878]/10"
                  : "border-white/10 bg-white/[0.02]"
              }`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <Icon size={14} style={{ color: cat.color }} />
                <span className="text-white/40 text-xs">WHEN</span>
                <select
                  value={r.input}
                  onChange={(e) => {
                    const nextInput = e.target.value;
                    patchRule(r.id, {
                      input: nextInput,
                      value: RULE_INPUTS[nextInput].options[0],
                    });
                  }}
                  className="bg-black border border-white/10 px-2 py-1 text-white text-xs"
                >
                  {Object.entries(RULE_INPUTS).map(([k, c]) => (
                    <option key={k} value={k}>
                      {c.label.toLowerCase()}
                    </option>
                  ))}
                </select>
                <span className="text-white/40 text-xs">=</span>
                <select
                  value={r.value}
                  onChange={(e) => patchRule(r.id, { value: e.target.value })}
                  className="bg-black border border-white/10 px-2 py-1 text-[#E8D6A3] text-xs"
                >
                  {cat.options.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
                <span className="text-white/40 text-xs">THEN</span>
                <select
                  value={r.action}
                  onChange={(e) => patchRule(r.id, { action: e.target.value })}
                  className={`bg-black border border-white/10 px-2 py-1 text-xs font-bold ${RULE_ACTIONS[r.action]?.color || ""}`}
                >
                  {Object.entries(RULE_ACTIONS).map(([k, a]) => (
                    <option key={k} value={k}>
                      {a.label}
                    </option>
                  ))}
                </select>
                {r.action === "show" || r.action === "boost" ? (
                  <input
                    placeholder="creative name / boost amount"
                    value={r.creative}
                    onChange={(e) => patchRule(r.id, { creative: e.target.value })}
                    className="bg-black border border-white/10 px-2 py-1 text-white text-xs flex-1 min-w-[120px]"
                  />
                ) : (
                  <span className="text-white/30 text-xs italic flex-1">no creative — pauses delivery</span>
                )}
                <button
                  type="button"
                  onClick={() => removeRule(r.id)}
                  aria-label="Remove"
                  data-testid={`${testidPrefix}-remove-${r.id}`}
                  className="text-white/40 hover:text-red-400 p-1"
                >
                  <X size={14} />
                </button>
              </div>
              {active && (
                <div className="mt-2 text-[10px] uppercase tracking-[0.22em] text-[#E8D6A3]">
                  ● firing right now in simulation
                </div>
              )}
            </div>
          );
        })}
      </div>

      {simulator && rules.length > 0 && (
        <div className="mt-4 border border-white/10 bg-black overflow-hidden">
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#00E5FF]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00E5FF] animate-pulse" />
              Live simulator
            </div>
            <button
              type="button"
              onClick={() => setRunning((r) => !r)}
              data-testid={`${testidPrefix}-toggle-run`}
              className="text-xs text-white/60 hover:text-white"
            >
              {running ? "Pause" : "Resume"}
            </button>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-5 gap-1 text-[10px] font-mono">
              {Object.entries(env).map(([k, v]) => (
                <div key={k} className="p-2 border border-white/10">
                  <div className="text-white/40 uppercase tracking-[0.18em] text-[9px]">{k}</div>
                  <div className="text-white mt-1 truncate">{v}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 p-4 border border-[#E8D6A3]/30 bg-gradient-to-br from-[#1a1508] to-[#0d0c0b] min-h-[80px] flex items-center justify-center">
              {activeRule ? (
                <div className="text-center">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#E8D6A3] mb-1">
                    {RULE_ACTIONS[activeRule.action]?.label} · {activeRule.input} = {activeRule.value}
                  </div>
                  <div className="font-display font-extrabold text-xl tracking-tight">
                    {activeRule.creative || "(no creative)"}
                  </div>
                </div>
              ) : (
                <div className="text-white/40 text-xs">
                  No rule matches this environment · falls back to default creative
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
